import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Resolve server-hosted asset paths (e.g. /uploads/logos/...) to the correct
// origin.  In Docker dev the client and server run on different ports, so
// relative paths must be prefixed with the server origin.
const SERVER_ORIGIN = (() => {
  const url = process.env.REACT_APP_API_URL;
  if (url && url.startsWith('http')) {
    return url.replace(/\/api\/?$/, '');
  }
  return '';
})();

export function assetUrl(path) {
  if (!path || !path.startsWith('/uploads/')) return path;
  return SERVER_ORIGIN + path;
}

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// ─── Request interceptor: attach JWT token ───────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('derbyscores_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Response interceptor: handle 401 globally ──────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('derbyscores_token');
      localStorage.removeItem('derbyscores_user');
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ─── Auth endpoints ──────────────────────────────────────────
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  resendVerification: () => api.post('/auth/resend-verification'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password })
};

// ─── Admin endpoints ─────────────────────────────────────────
export const adminApi = {
  getDashboard: () => api.get('/admin/dashboard'),
  getEvents: (params) => api.get('/admin/events', { params }),
  updateEventPaymentStatus: (id, data) => api.patch(`/admin/events/${id}/payment-status`, data),
  updateEventOrganizer: (id, data) => api.patch(`/admin/events/${id}/organizer`, data),
  getOrganizers: (params) => api.get('/admin/organizers', { params }),
  getOrganizer: (id) => api.get(`/admin/organizers/${id}`),
  createOrganizer: (data) => api.post('/admin/organizers', data),
  updateOrganizer: (id, data) => api.put(`/admin/organizers/${id}`, data),
  updateOrganizerStatus: (id, data) => api.patch(`/admin/organizers/${id}/status`, data),
  getParticipants: (params) => api.get('/admin/participants', { params }),
  createParticipant: (data) => api.post('/admin/participants', data),
  updateParticipant: (id, data) => api.put(`/admin/participants/${id}`, data),
  updateParticipantStatus: (id, data) => api.patch(`/admin/participants/${id}/status`, data),
  getPricingTiers: () => api.get('/admin/pricing-tiers'),
  createPricingTier: (data) => api.post('/admin/pricing-tiers', data),
  updatePricingTier: (id, data) => api.put(`/admin/pricing-tiers/${id}`, data),
  deletePricingTier: (id) => api.delete(`/admin/pricing-tiers/${id}`),
  getCoupons: () => api.get('/admin/coupons'),
  createCoupon: (data) => api.post('/admin/coupons', data),
  updateCoupon: (id, data) => api.put(`/admin/coupons/${id}`, data),
  deleteCoupon: (id) => api.delete(`/admin/coupons/${id}`),
  updateTheme: (data) => api.put('/admin/theme', data),
  uploadLogo: (formData) => api.post('/admin/theme/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteLogo: () => api.delete('/admin/theme/logo'),
  resetTheme: () => api.post('/admin/theme/reset'),
  getEmailTemplates: () => api.get('/admin/email-templates'),
  updateEmailTemplate: (id, data) => api.put(`/admin/email-templates/${id}`, data),
  testEmailTemplate: (id) => api.post(`/admin/email-templates/${id}/test`),
  deleteOrganizer: (id) => api.delete(`/admin/organizers/${id}`),
  deleteParticipant: (id) => api.delete(`/admin/participants/${id}`),
  getTeams: (params) => api.get('/admin/teams', { params }),
  getTeam: (id) => api.get(`/admin/teams/${id}`),
  updateTeam: (id, data) => api.put(`/admin/teams/${id}`, data),
  createTeam: (data) => api.post('/admin/teams', data),
  deleteTeam: (id) => api.delete(`/admin/teams/${id}`),
  getParticipantDetail: (id) => api.get(`/admin/participants/${id}/detail`),
  getAuditLog: (params) => api.get('/admin/audit-log', { params }),
  getAuditLogActors: () => api.get('/admin/audit-log/actors'),
  exportAuditLog: (params) => api.get('/admin/audit-log/export', { params, responseType: 'blob' }),
  getSponsors: (params) => api.get('/admin/sponsors', { params }),
  createSponsor: (data) => api.post('/admin/sponsors', data),
  updateSponsor: (id, data) => api.put(`/admin/sponsors/${id}`, data),
  deleteSponsor: (id) => api.delete(`/admin/sponsors/${id}`),
  uploadSponsorLogo: (formData) => api.post('/admin/sponsors/upload-logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Advert Boards
  getAdvertBoards: () => api.get('/admin/advert-boards'),
  createAdvertBoard: (data) => api.post('/admin/advert-boards', data),
  updateAdvertBoard: (id, data) => api.put(`/admin/advert-boards/${id}`, data),
  deleteAdvertBoard: (id) => api.delete(`/admin/advert-boards/${id}`),
  getAdvertBoardAssignments: (id) => api.get(`/admin/advert-boards/${id}/assignments`),
  createAdvertBoardAssignment: (id, data) => api.post(`/admin/advert-boards/${id}/assignments`, data),
  deleteAdvertBoardAssignment: (id) => api.delete(`/admin/advert-board-assignments/${id}`)
};

// ─── Events endpoints ────────────────────────────────────────
export const eventsApi = {
  getEvents: () => api.get('/events'),
  getEvent: (id) => api.get(`/events/${id}`),
  createEvent: (data) => api.post('/events', data),
  updateEvent: (id, data) => api.put(`/events/${id}`, data),
  payEvent: (id, data) => api.post(`/events/${id}/pay`, data),
  upgradeTier: (id, data) => api.post(`/events/${id}/upgrade-tier`, data),
  updateStatus: (id, data) => api.patch(`/events/${id}/status`, data),
  register: (id, data) => api.post(`/events/${id}/register`, data),
  signWaiver: (id, data) => api.post(`/events/${id}/sign-waiver`, data),
  getRegistrations: (id) => api.get(`/events/${id}/registrations`),
  getPricingTiers: () => api.get('/events/pricing-tiers'),
  validateCoupon: (data) => api.post('/events/validate-coupon', data),
  deleteEvent: (id) => api.delete(`/events/${id}`),
  uploadFeaturedImage: (id, formData) => api.post(`/events/${id}/featured-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteFeaturedImage: (id) => api.delete(`/events/${id}/featured-image`),
  getEventReport: (id) => api.get(`/events/${id}/report`),
  getMyRegistration: (id) => api.get(`/events/${id}/my-registration`),
  updateRegistrationStatus: (eventId, registrationId, status, teamId, memberRole) =>
    api.patch(`/events/${eventId}/registrations/${registrationId}/status`, { status, team_id: teamId || undefined, member_role: memberRole || undefined }),
  updateRegistrationDetails: (eventId, registrationId, data) =>
    api.patch(`/events/${eventId}/registrations/${registrationId}/details`, data),
  removeRegistration: (eventId, registrationId) =>
    api.delete(`/events/${eventId}/registrations/${registrationId}`)
};

// ─── Invoice endpoints ───────────────────────────────────────
export const invoiceApi = {
  getMyInvoices: () => api.get('/invoices/my-invoices'),
  getInvoiceByEvent: (eventId) => api.get(`/invoices/event/${eventId}`),
  downloadInvoice: async (invoiceId) => {
    const response = await api.get(`/invoices/${invoiceId}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `invoice-${invoiceId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  // Admin endpoints
  getAllInvoices: (params) => api.get('/invoices/admin/all', { params }),
  getRevenueStats: (params) => api.get('/invoices/admin/stats', { params }),
  regenerateInvoice: (invoiceId) => api.post(`/invoices/regenerate/${invoiceId}`),
  generateForEvent: (eventId) => api.post(`/invoices/admin/generate/${eventId}`)
};

// ─── Tournament & Scoring endpoints ──────────────────────────
export const tournamentApi = {
  getTournaments: (eventId) => api.get(`/events/${eventId}/tournaments`),
  createTournament: (eventId, data) => api.post(`/events/${eventId}/tournaments`, data),
  importTournaments: (eventId, sourceTournamentIds) => api.post(`/events/${eventId}/tournaments/import`, { source_tournament_ids: sourceTournamentIds }),
  updateTournament: (eventId, tournId, data) => api.put(`/events/${eventId}/tournaments/${tournId}`, data),
  deleteTournament: (eventId, tournId) => api.delete(`/events/${eventId}/tournaments/${tournId}`),
  toggleScoreboardVisible: (eventId, tournId, visible) => api.patch(`/events/${eventId}/tournaments/${tournId}/scoreboard-visible`, { visible }),
  logCatch: (eventId, data) => api.post(`/events/${eventId}/catches`, data),
  getCatches: (eventId, params) => api.get(`/events/${eventId}/catches`, { params }),
  updateCatch: (eventId, catchId, data) => api.put(`/events/${eventId}/catches/${catchId}`, data),
  deleteCatch: (eventId, catchId) => api.delete(`/events/${eventId}/catches/${catchId}`),
  invalidateCatch: (eventId, catchId, data) => api.patch(`/events/${eventId}/catches/${catchId}/invalidate`, data),
  getScoreboard: (eventId) => api.get(`/events/${eventId}/scoreboard`),
  getTeams: (eventId) => api.get(`/events/${eventId}/teams`),
  getEnrolledParticipants: (eventId) => api.get(`/events/${eventId}/participants/enrolled`),
  updateParticipantTournaments: (eventId, participantId, data) => api.patch(`/events/${eventId}/participants/${participantId}/tournaments`, data),
  createTeam: (eventId, data) => api.post(`/events/${eventId}/teams`, data),
  updateTeam: (eventId, teamId, data) => api.patch(`/events/${eventId}/teams/${teamId}`, data),
  updateTeamTournaments: (eventId, teamId, data) => api.patch(`/events/${eventId}/teams/${teamId}`, data),
  removeTeam: (eventId, teamId) => api.delete(`/events/${eventId}/teams/${teamId}`),
  bulkAddTeams: (eventId, data) => api.post(`/events/${eventId}/teams/bulk-add`, data),
  addTeamMember: (eventId, teamId, data) => api.post(`/events/${eventId}/teams/${teamId}/members`, data),
  removeTeamMember: (eventId, teamId, userId) => api.delete(`/events/${eventId}/teams/${teamId}/members/${userId}`),
  addSponsor: (eventId, data) => api.post(`/events/${eventId}/sponsors`, data),
  updateSponsor: (eventId, sponsorId, data) => api.patch(`/events/${eventId}/sponsors/${sponsorId}`, data),
  removeSponsor: (eventId, sponsorId) => api.delete(`/events/${eventId}/sponsors/${sponsorId}`),
  getSavedSponsors: (eventId) => api.get(`/events/${eventId}/sponsors/saved`),
  deleteSavedSponsor: (eventId, id) => api.delete(`/events/${eventId}/sponsors/saved/${id}`),
  searchSponsors: (eventId, q) => api.get(`/events/${eventId}/sponsors/search`, { params: { q } }),
  uploadSponsorLogo: (eventId, file) => { const fd = new FormData(); fd.append('logo', file); return api.post(`/events/${eventId}/sponsors/upload-logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  bulkAddSponsors: (eventId, data) => api.post(`/events/${eventId}/sponsors/bulk-add`, data),
  // Species groups (custom tournaments)
  getSpeciesGroups: (eventId, tournId) => api.get(`/events/${eventId}/tournaments/${tournId}/species-groups`),
  updateSpeciesGroups: (eventId, tournId, data) => api.put(`/events/${eventId}/tournaments/${tournId}/species-groups`, data),
  // Rollover (custom tournaments)
  toggleRollover: (eventId, tournId, is_rolled_over) => api.patch(`/events/${eventId}/tournaments/${tournId}/rollover`, { is_rolled_over }),
};

// ─── Payment ─────────────────────────────────────────────────
export const paymentApi = {
  getSquareAppId: () => api.get('/payments/square-app-id'),
  payForEvent: (eventId, sourceToken) => api.post(`/events/${eventId}/pay`, { sourceToken })
};

// ─── Theme (public, no auth) ────────────────────────────────
export const themeApi = {
  getTheme: () => api.get('/theme')
};

// ─── Circuit Rankings (public + admin) ──────────────────────
export const circuitApi = {
  getStandings: (params) => api.get('/circuit/standings', { params }),
  getRegions: (params) => api.get('/circuit/regions', { params }),
  getStats: (params) => api.get('/circuit/stats', { params }),
  getTeamHistory: (teamId, params) => api.get(`/circuit/teams/${teamId}/history`, { params }),
  recordResult: (data) => api.post('/circuit/results', data),
  recalculate: (data) => api.post('/circuit/recalculate', data),
  backfill: (eventId) => api.post(`/circuit/backfill/${eventId}`),
};

// ─── Bodies of Water (admin CRUD + public list) ──────────────
export const bodiesOfWaterApi = {
  list: () => api.get('/admin/bodies-of-water'),
  listPublic: () => api.get('/events/bodies-of-water'),
  create: (data) => api.post('/admin/bodies-of-water', data),
  update: (id, data) => api.put(`/admin/bodies-of-water/${id}`, data),
  delete: (id) => api.delete(`/admin/bodies-of-water/${id}`)
};

// ─── Public endpoints (no auth — landing page) ──────────────
export const publicApi = {
  getEvents: () => api.get('/public/events'),
  getEvent: (id) => api.get(`/public/events/${id}`),
  getPricingTiers: () => api.get('/public/pricing-tiers'),
};

// ─── Analytics endpoints ─────────────────────────────────────
export const analyticsApi = {
  getEventSummary: (eventId) => api.get(`/analytics/event/${eventId}/summary`),
  getSponsorReport: (eventId, sponsorId) =>
    api.get(`/analytics/event/${eventId}/sponsor/${sponsorId}`)
};

// ─── Admin Analytics (platform-wide) ────────────────────────
export const adminAnalyticsApi = {
  getSummary: (startDate, endDate) =>
    api.get(`/admin/analytics/summary?start_date=${startDate}&end_date=${endDate}`)
};

export default api;

// ─── Teams (participant join requests) ───────────────────────
export const teamsApi = {
  browse: (params) => api.get('/events/teams/browse', { params }),
  requestJoin: (teamId, data) => api.post(`/events/teams/${teamId}/join-request`, data),
  myRequests: () => api.get('/events/teams/my-requests'),
  cancelRequest: (id) => api.delete(`/events/teams/join-requests/${id}`),
  pendingRequests: () => api.get('/events/teams/pending-requests'),
  respondToRequest: (id, data) => api.put(`/events/teams/join-requests/${id}/respond`, data),
  myTeams: () => api.get('/events/teams/my-teams'),
  createTeam: (data) => api.post('/events/teams/create', data),
  invite: (teamId, data) => api.post(`/events/teams/${teamId}/invite`, data),
  cancelInvitation: (id) => api.delete(`/events/teams/invitations/${id}`),
  removeMember: (teamId, userId) => api.delete(`/events/teams/${teamId}/members/${userId}`),
  getInvitations: () => api.get('/events/teams/my-invitations'),
  acceptInvitation: (token) => api.post(`/events/teams/invitations/${token}/accept`),
};

// ─── Team Admin (organizer / master_admin) ───────────────────
export const teamAdminApi = {
  getStatus: (eventId) => api.get(`/events/${eventId}/admin/team-status`),
  approveRequest: (eventId, requestId, makeCaptain) =>
    api.post(`/events/${eventId}/admin/join-requests/${requestId}/approve`, { makeCaptain }),
  denyRequest: (eventId, requestId, reason) =>
    api.post(`/events/${eventId}/admin/join-requests/${requestId}/deny`, { reason }),
  assignCaptain: (eventId, teamId, userId) =>
    api.post(`/events/${eventId}/admin/teams/${teamId}/assign-captain`, { user_id: userId }),
  assignNewCaptain: (eventId, teamId, name, email) =>
    api.post(`/events/${eventId}/admin/teams/${teamId}/assign-captain`, { new_name: name, new_email: email }),
  notifyUnassigned: (eventId) =>
    api.post(`/events/${eventId}/admin/notify-unassigned`)
};

// ─── Me API (participant) ─────────────────────────────────────
export const meApi = {
  getTeamStatus: () => api.get('/auth/me/team-status')
};

// ─── SEARCH (global DB autocomplete) ─────────────────────────
export const searchApi = {
  searchUsers: (q) => api.get(`/events/search/users?q=${encodeURIComponent(q)}`),
  searchTeams: (q) => api.get(`/events/search/teams?q=${encodeURIComponent(q)}`),
  getAllTeams: (excludeEventId, q) => {
    const params = new URLSearchParams();
    if (excludeEventId) params.set('excludeEventId', excludeEventId);
    if (q) params.set('q', q);
    return api.get(`/events/all-teams?${params.toString()}`);
  },
  addParticipant: (eventId, data) => api.post(`/events/${eventId}/participants`, data),
  getAllParticipants: (excludeEventId, q) => {
    const params = new URLSearchParams();
    if (excludeEventId) params.set('excludeEventId', excludeEventId);
    if (q) params.set('q', q);
    return api.get(`/events/all-participants?${params.toString()}`);
  },
  bulkAddParticipants: (eventId, data) => api.post(`/events/${eventId}/participants/bulk-add`, data),
};
